// Product page: gallery (stage, thumbnail row, glow layer, hover lens, full-screen viewer with pinch zoom), drawer photo
// hydration, multi-buy cards and lamp picker, pair-it module, quick view (?view=am-quick fragments, prefetched and cached),
// "Light it up" slider, sticky buy bar, delivery estimate (cut-off, bank holidays, international window), back-in-stock,
// recently viewed.
const AM = window.AM;
const root = document.querySelector("[data-am-pdp]");
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const behavior = reducedMotion ? "auto" : "smooth";
const desktop = window.matchMedia("(min-width: 1024px)");
const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/* Photos after the first carry their real sources in data attributes, so nothing competes with the LCP photo.
   hydrate() moves them into place, one photo at a time. */
function hydrate(img, srcKey = "src", setKey = "srcset") {
  if (!img || !img.dataset[setKey]) return false;
  // A <picture> slide carries its phone candidates in a <source> beside the img: that source fills first, so the
  // browser picks from the right list the moment the img changes.
  img.parentElement?.querySelectorAll("source[data-srcset]").forEach((source) => {
    source.srcset = source.dataset.srcset;
    delete source.dataset.srcset;
  });
  img.srcset = img.dataset[setKey];
  img.src = img.dataset[srcKey];
  delete img.dataset[setKey];
  delete img.dataset[srcKey];
  return true;
}

/* Drawers flagged data-am-hydrate (the "All designs" drawer) fill their thumbnails ahead of the tap, so they are on screen
   the moment the drawer opens: at low priority once the page has loaded and a trigger is within 200px of the viewport
   (when the browser is idle), at once on hover, focus or touch of a trigger, and on open at the latest. Images load
   inside the closed dialog because they are not lazy. Save-Data visitors skip the scroll warm-up. */
const fillDrawer = (dialog, priority) => {
  dialog.querySelectorAll("img[data-srcset]").forEach((img) => {
    if (priority) img.fetchPriority = priority;
    hydrate(img);
  });
};
document.addEventListener("am:open", (event) => {
  const dialog = event.target;
  if (dialog instanceof Element && dialog.matches("[data-am-hydrate]")) fillDrawer(dialog);
});
document.querySelectorAll("dialog[data-am-hydrate][id]").forEach((dialog) => {
  const selector = `[data-am-open="${dialog.id}"]`;
  const triggers = [...document.querySelectorAll(selector)];
  if (!triggers.length) return;
  let io = null;
  const intents = ["pointerover", "touchstart", "focusin"];
  const warm = (priority) => {
    io?.disconnect();
    intents.forEach((type) => document.removeEventListener(type, onIntent));
    fillDrawer(dialog, priority);
  };
  function onIntent(event) {
    if (event.target.closest?.(selector)) warm("high");
  }
  intents.forEach((type) => document.addEventListener(type, onIntent, { passive: true }));
  if (navigator.connection?.saveData || !("IntersectionObserver" in window)) return;
  const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 200));
  io = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    io.disconnect();
    const go = () => idle(() => warm("low"), { timeout: 2000 });
    if (document.readyState === "complete") go();
    else window.addEventListener("load", go, { once: true });
  }, { rootMargin: "200px 0px" });
  triggers.forEach((trigger) => io.observe(trigger));
});

function initGallery() {
  const gallery = root?.querySelector("[data-am-gallery]");
  if (!gallery) return;
  const stage = gallery.querySelector(".am-gallery__stage");
  const track = gallery.querySelector("[data-am-gallery-track]");
  const slides = [...track.querySelectorAll(".am-gallery__slide")];
  const rail = gallery.querySelector("[data-am-gallery-rail]");
  const thumbs = [...gallery.querySelectorAll("[data-am-thumb]")];
  const current = gallery.querySelector("[data-am-gallery-current]");
  const lens = gallery.querySelector("[data-am-gallery-lens]");
  const prevArrow = gallery.querySelector('[data-am-gallery-step="-1"]');
  const nextArrow = gallery.querySelector('[data-am-gallery-step="1"]');
  const glow = gallery.querySelector("[data-am-gallery-glow]");
  const count = slides.length;
  let index = 0;
  let lensOn = false;

  const fill = (i) => {
    const img = slides[i]?.querySelector("img[data-srcset]");
    if (img) hydrate(img);
  };
  // The shown photo plus one each side, so the next swipe or arrow press never waits for the network.
  const prepare = (i) => {
    fill(i);
    fill(i + 1);
    fill(i - 1);
  };

  const setActive = (i) => {
    index = i;
    thumbs.forEach((thumb, t) => {
      if (t === i) thumb.setAttribute("aria-current", "true");
      else thumb.removeAttribute("aria-current");
    });
    if (current) current.textContent = String(i + 1);
    if (prevArrow) prevArrow.disabled = i === 0;
    if (nextArrow) nextArrow.disabled = i === count - 1;
    // The halo behind the stage follows the photo: AM.glow reads the 16px copy's colour (cached) and skips dark or grey photos.
    const tint = slides[i]?.dataset.glow;
    if (glow && tint && glow.dataset.glowSrc !== tint) AM.glow.paint(glow, tint);
    // Keep the active thumbnail inside the strip without moving the page.
    const thumb = thumbs[i];
    if (rail && thumb && rail.scrollWidth > rail.clientWidth) {
      rail.scrollTo({ left: thumb.offsetLeft - rail.clientWidth / 2 + thumb.offsetWidth / 2, behavior });
    }
    if (lensOn) paintLens();
  };

  const show = (i, smooth = true) => {
    const target = clamp(i, 0, count - 1);
    prepare(target);
    const slide = slides[target];
    if (!slide) return;
    track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? behavior : "auto" });
    setActive(target);
  };

  // The next photo warms up once the first (LCP) photo has arrived; the rest follow as they are reached.
  const hero = track.querySelector(".am-gallery__hero");
  const warm = () => window.setTimeout(() => fill(1), 0);
  if (hero && !hero.complete) hero.addEventListener("load", warm, { once: true });
  else warm();
  track.addEventListener("pointerdown", () => prepare(index + 1), { once: true, passive: true });

  thumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => show(Number(thumb.dataset.amThumb)));
    thumb.addEventListener("pointerenter", (event) => {
      if (event.pointerType !== "mouse" || !desktop.matches) return;
      show(Number(thumb.dataset.amThumb));
    });
  });
  // Arrow keys move through the rail as well as the stage.
  rail?.addEventListener("keydown", (event) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = clamp(index + step, 0, count - 1);
    thumbs[next]?.focus();
    show(next);
  });
  gallery.addEventListener("click", (event) => {
    const step = event.target.closest("[data-am-gallery-step]");
    if (step) show(index + Number(step.dataset.amGalleryStep));
  });
  track.addEventListener("keydown", (event) => {
    const moves = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: count - 1 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    show(moves[event.key]);
  });

  if ("IntersectionObserver" in window && count > 1) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (desktop.matches) return; // desktop moves only through show(), so a smooth scroll never flickers the rail
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = Number(entry.target.dataset.index);
          prepare(i);
          setActive(i);
        }
      },
      { root: track, threshold: 0.6 },
    );
    slides.forEach((slide) => observer.observe(slide));
  }
  // Desktop and phones lay the track out differently; land on the active photo again after a resize.
  desktop.addEventListener("change", () => show(index, false));

  /* Hover lens (mouse only): a 2x view of the active photo that follows the cursor. */
  function paintLens() {
    const slide = slides[index];
    if (!lens || !slide || slide.dataset.role === "dim") {
      lens?.classList.remove("is-on");
      return;
    }
    const url = `url("${slide.dataset.zoom}")`;
    if (lens.style.backgroundImage !== url) lens.style.backgroundImage = url;
    lens.classList.add("is-on");
  }
  if (lens && stage && finePointer.matches) {
    lens.hidden = false;
    const place = (event) => {
      const rect = stage.getBoundingClientRect();
      const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      lens.style.backgroundPosition = `${x}% ${y}%`;
    };
    stage.addEventListener("pointerenter", (event) => {
      if (event.pointerType !== "mouse" || !desktop.matches) return;
      lensOn = true;
      place(event);
      paintLens();
    });
    stage.addEventListener("pointermove", (event) => {
      // The pointer can already be over the stage when the page loads (no pointerenter then): the first move switches the lens on.
      if (!lensOn) {
        if (event.pointerType !== "mouse" || !desktop.matches) return;
        lensOn = true;
      }
      place(event);
      // Over an arrow the lens steps aside, so the button reads as a button.
      if (event.target.closest("[data-am-gallery-step]")) lens.classList.remove("is-on");
      else paintLens();
    });
    stage.addEventListener("pointerleave", () => {
      lensOn = false;
      lens.classList.remove("is-on");
    });
  }

  /* Full-screen viewer: large photos fetched per slide, pinch or double-tap to zoom, drag to pan, arrows and keys. */
  const zoom = document.getElementById("am-zoom");
  if (!zoom) return;
  const zoomTrack = zoom.querySelector("[data-am-zoom-track]");
  const zoomSlides = [...zoomTrack.children];
  const zoomThumbs = [...zoom.querySelectorAll("[data-am-zoom-thumb]")];
  const zoomCurrent = zoom.querySelector("[data-am-zoom-current]");
  const zoomToggle = zoom.querySelector("[data-am-zoom-toggle]");
  const prev = zoom.querySelector('[data-am-zoom-step="-1"]');
  const next = zoom.querySelector('[data-am-zoom-step="1"]');
  let zoomIndex = 0;
  let pic = null;
  let scale = 1;
  let tx = 0;
  let ty = 0;
  const pointers = new Map();
  let pinch = null;
  let drag = null;
  let lastTap = 0;

  const zoomFill = (i) => {
    const img = zoomSlides[i]?.querySelector("img[data-zoom-srcset]");
    if (img) hydrate(img, "zoomSrc", "zoomSrcset");
  };
  const paint = () => {
    if (pic) pic.style.transform = scale > 1 ? `translate(${tx}px, ${ty}px) scale(${scale})` : "";
    const zoomed = scale > 1;
    zoom.classList.toggle("is-zoomed", zoomed);
    if (zoomToggle) {
      zoomToggle.setAttribute("aria-pressed", String(zoomed));
      zoomToggle.setAttribute("aria-label", zoomed ? "Zoom out" : "Zoom in");
    }
  };
  const resetZoom = () => {
    if (pic) pic.style.transform = "";
    pic = null;
    scale = 1;
    tx = 0;
    ty = 0;
    pinch = null;
    drag = null;
    pointers.clear();
    paint();
  };
  // Keep the enlarged photo inside its slide.
  const limit = () => {
    if (!pic) return;
    const box = pic.parentElement.getBoundingClientRect();
    const maxX = Math.max(0, (pic.offsetWidth * scale - box.width) / 2);
    const maxY = Math.max(0, (pic.offsetHeight * scale - box.height) / 2);
    tx = clamp(tx, -maxX, maxX);
    ty = clamp(ty, -maxY, maxY);
  };
  const zoomAt = (target, clientX, clientY, factor = 2.5) => {
    pic = target;
    const rect = pic.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    scale = factor;
    tx = -(clientX - cx) * (factor - 1);
    ty = -(clientY - cy) * (factor - 1);
    limit();
    paint();
  };
  const toggleZoom = (target, clientX, clientY) => {
    if (scale > 1) resetZoom();
    else zoomAt(target, clientX, clientY);
  };

  const markZoom = (i) => {
    zoomIndex = i;
    if (zoomCurrent) zoomCurrent.textContent = String(i + 1);
    if (prev) prev.disabled = i === 0;
    if (next) next.disabled = i === zoomSlides.length - 1;
    zoomThumbs.forEach((thumb, t) => {
      if (t === i) thumb.setAttribute("aria-current", "true");
      else thumb.removeAttribute("aria-current");
    });
    zoomFill(i);
    zoomFill(i + 1);
    zoomFill(i - 1);
  };
  const goTo = (i, smooth = true) => {
    const target = zoomSlides[clamp(i, 0, zoomSlides.length - 1)];
    if (!target) return;
    resetZoom();
    zoomTrack.scrollTo({ left: target.offsetLeft, behavior: smooth ? behavior : "auto" });
    markZoom(Number(target.dataset.index));
  };

  gallery.addEventListener("click", (event) => {
    const button = event.target.closest("[data-am-zoom]");
    if (!button) return;
    AM.openDialog("am-zoom", button);
    const i = Number(button.dataset.amZoom);
    markZoom(i);
    window.requestAnimationFrame(() => goTo(i, false));
  });
  zoom.addEventListener("close", resetZoom);
  zoom.addEventListener("click", (event) => {
    const step = event.target.closest("[data-am-zoom-step]");
    if (step) return goTo(zoomIndex + Number(step.dataset.amZoomStep));
    const thumb = event.target.closest("[data-am-zoom-thumb]");
    if (thumb) return goTo(Number(thumb.dataset.amZoomThumb));
    if (event.target.closest("[data-am-zoom-toggle]")) {
      const target = zoomSlides[zoomIndex]?.querySelector("[data-am-zoom-pic]");
      if (!target) return;
      const rect = target.getBoundingClientRect();
      toggleZoom(target, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
  });
  zoom.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      goTo(zoomIndex + (event.key === "ArrowRight" ? 1 : -1));
    } else if (event.key === "+" || event.key === "=" || event.key === "-") {
      const target = zoomSlides[zoomIndex]?.querySelector("[data-am-zoom-pic]");
      if (!target) return;
      event.preventDefault();
      const rect = target.getBoundingClientRect();
      if (event.key === "-") resetZoom();
      else zoomAt(target, rect.left + rect.width / 2, rect.top + rect.height / 2, Math.min(4, (scale > 1 ? scale : 1) + 1));
    }
  });

  // Pinch (two pointers), drag when enlarged (one pointer), double-tap or double-click to toggle.
  zoomTrack.addEventListener("pointerdown", (event) => {
    const target = event.target.closest("[data-am-zoom-pic]");
    if (!target) return;
    if (pic && pic !== target) resetZoom();
    pic = target;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, scale };
      drag = null;
    } else if (scale > 1) {
      drag = { x: event.clientX - tx, y: event.clientY - ty, moved: false };
      try { target.setPointerCapture(event.pointerId); } catch { /* nothing to capture */ }
      event.preventDefault();
    }
  });
  zoomTrack.addEventListener("pointermove", (event) => {
    if (!pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size === 2 && pinch) {
      const [a, b] = [...pointers.values()];
      scale = clamp((pinch.scale * Math.hypot(a.x - b.x, a.y - b.y)) / pinch.dist, 1, 4);
      limit();
      paint();
    } else if (drag && scale > 1) {
      tx = event.clientX - drag.x;
      ty = event.clientY - drag.y;
      drag.moved = true;
      limit();
      paint();
    }
  });
  const release = (event) => {
    const start = pointers.get(event.pointerId);
    pointers.delete(event.pointerId);
    if (pointers.size < 2) pinch = null;
    if (pointers.size) return;
    const moved = drag?.moved || (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8);
    drag = null;
    if (scale < 1.05 && scale !== 1) {
      scale = 1;
      tx = 0;
      ty = 0;
      paint();
    }
    if (event.type === "pointerup" && !moved) {
      const target = event.target.closest("[data-am-zoom-pic]");
      // One mouse click zooms in at the pointer (and a click zooms out again); touch keeps the double-tap so a single
      // tap stays free for swiping between photos.
      if (event.pointerType === "mouse") {
        if (target) toggleZoom(target, event.clientX, event.clientY);
        return;
      }
      const now = event.timeStamp;
      if (now - lastTap < 320) {
        lastTap = 0;
        if (target) toggleZoom(target, event.clientX, event.clientY);
      } else lastTap = now;
    }
  };
  zoomTrack.addEventListener("pointerup", release);
  zoomTrack.addEventListener("pointercancel", release);
  zoomTrack.addEventListener("dragstart", (event) => event.preventDefault());
  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) markZoom(Number(entry.target.dataset.index));
        }
      },
      { root: zoomTrack, threshold: 0.6 },
    );
    zoomSlides.forEach((slide) => observer.observe(slide));
  }
}

/* Multi-buy: 1 / 2 / 3+ lamps. Extra lamps are hidden checked data-am-addon inputs in the product form, so am-core's
   data-am-add handler posts every chosen variant in one request. Each slot opens the picker to swap that lamp. */
function initMultibuy() {
  const box = document.querySelector("[data-am-multibuy]");
  if (!box) return;
  const form = box.closest("form");
  const tier2 = Number(box.dataset.tier2) || 0;
  const tier3 = Number(box.dataset.tier3) || 0;
  const base = { variant: form.querySelector('input[name="id"]').value, price: Number(box.dataset.price), title: box.dataset.title, image: box.dataset.image };
  const slotsWrap = box.querySelector("[data-am-slots]");
  const slots = [...slotsWrap.querySelectorAll("[data-am-slot]")];
  const stepper = box.querySelector("[data-am-mb-stepper]");
  const stepperWrap = box.querySelector("[data-am-mb-stepper-wrap]");
  const stepDown = box.querySelector('[data-am-mb-step="-1"]');
  const stepUp = box.querySelector('[data-am-mb-step="1"]');
  const countOut = box.querySelector("[data-am-mb-count]");
  const picker = document.getElementById("am-picker");
  const extras = []; // chosen lamps beyond the first: { variant, price, title, image }

  const tier = () => Number(box.querySelector('input[name="am-tier"]:checked')?.value || 1);
  const stepValue = () => {
    const value = Math.min(10, Math.max(3, Math.round(Number(stepper?.value) || 3)));
    if (stepper && stepper.value !== String(value)) stepper.value = String(value);
    return value;
  };
  const count = () => (tier() === 3 ? stepValue() : tier());
  const percent = (n) => (n >= 3 ? tier3 : n === 2 ? tier2 : 0);
  const discounted = (price, pct) => Math.round((price * (100 - pct)) / 100);
  const totals = (lamps) => {
    const pct = percent(lamps.length);
    const full = lamps.reduce((sum, lamp) => sum + lamp.price, 0);
    const total = lamps.reduce((sum, lamp) => sum + discounted(lamp.price, pct), 0);
    return { full, total, save: full - total, pct };
  };
  // The lamps a tier would hold: the chosen extras so far, this lamp for the rest.
  const lampsFor = (n) => {
    const list = [base];
    for (let i = 0; i < n - 1; i += 1) list.push(extras[i] || base);
    return list;
  };

  function renderTiles(n) {
    for (const t of [2, 3]) {
      const lamps = lampsFor(t === 3 ? Math.max(3, n) : 2);
      const { full, total, save, pct } = totals(lamps);
      const set = (field, text) => {
        const el = box.querySelector(`[data-am-mb-${field}="${t}"]`);
        if (el) el.textContent = text;
      };
      set("full", AM.formatMoney(full));
      set("total", AM.formatMoney(total));
      set("save", `Save ${AM.formatMoney(save)}`);
      // Mixed lamps have no single per-lamp price (it depends on which lamp is which), so say the discount instead.
      const mixed = lamps.some((lamp) => lamp.price !== base.price);
      set("each", mixed ? `${pct}% off` : AM.formatMoney(discounted(base.price, pct)));
      lamps.slice(0, 3).forEach((lamp, i) => {
        const img = box.querySelector(`[data-am-mb-img="${t}:${i + 1}"]`);
        if (img && lamp.image && img.getAttribute("src") !== lamp.image) img.src = lamp.image;
      });
    }
  }

  function renderSlots(n) {
    slotsWrap.hidden = n < 2;
    slots.forEach((el, i) => {
      const lamp = extras[i];
      el.hidden = i >= n - 1;
      if (!lamp) return;
      el.dataset.variant = lamp.variant;
      el.dataset.price = String(lamp.price);
      el.dataset.title = lamp.title;
      el.querySelector("[data-am-slot-title]").textContent = lamp.variant === base.variant ? "Same lamp" : lamp.title;
      const img = el.querySelector("[data-am-slot-img]");
      if (img && lamp.image && img.getAttribute("src") !== lamp.image) img.src = lamp.image;
    });
  }

  function sync() {
    const n = count();
    while (extras.length < n - 1) extras.push({ ...base });
    extras.length = n - 1;
    // Copies of this lamp go on the main line's quantity; other lamps become hidden checked addon inputs,
    // one each, which am-core's data-am-add handler posts with the main variant in one request.
    const quantity = form.querySelector('input[name="quantity"]');
    if (quantity) quantity.value = String(1 + extras.filter((lamp) => lamp.variant === base.variant).length);
    form.querySelectorAll("input[data-am-mb-addon]").forEach((el) => el.remove());
    for (const lamp of extras) {
      if (lamp.variant === base.variant) continue;
      const input = document.createElement("input");
      Object.assign(input, { type: "checkbox", checked: true, hidden: true, value: lamp.variant, tabIndex: -1 });
      input.setAttribute("data-am-addon", "");
      input.setAttribute("data-am-mb-addon", "");
      form.append(input);
    }
    if (stepperWrap) stepperWrap.hidden = tier() !== 3;
    if (stepDown) stepDown.disabled = n <= 3;
    if (stepUp) stepUp.disabled = n >= 10;
    if (countOut) countOut.textContent = String(n);
    renderSlots(n);
    renderTiles(n);
    const { full, total, save } = totals([base, ...extras]);
    box.dispatchEvent(new CustomEvent("am:multibuy", { bubbles: true, detail: { count: n, total, full, save, mode: "multi" } }));
  }

  let editing = -1;
  slotsWrap.addEventListener("click", (event) => {
    const slot = event.target.closest("[data-am-slot]");
    if (!slot) return;
    editing = slots.indexOf(slot);
    const number = picker?.querySelector("[data-am-picker-n]");
    if (number) number.textContent = String(editing + 2);
    AM.openDialog("am-picker", slot);
  });
  picker?.addEventListener("click", (event) => {
    const pick = event.target.closest("[data-am-pick]");
    if (!pick || editing < 0) return;
    extras[editing] = { variant: pick.dataset.variant, price: Number(pick.dataset.price), title: pick.dataset.title, image: pick.dataset.image };
    AM.closeDialog(pick.closest("dialog"));
    sync();
    AM.announce(`Lamp ${editing + 2}: ${pick.dataset.title}`);
  });
  box.addEventListener("change", sync);
  box.addEventListener("click", (event) => {
    const step = event.target.closest("[data-am-mb-step]");
    if (!step || !stepper) return;
    stepper.value = String(Math.min(10, Math.max(3, (Number(stepper.value) || 3) + Number(step.dataset.amMbStep))));
    sync();
  });
  stepper?.addEventListener("input", sync);
  sync();
}

/* Statement pieces: tick single lamps with the same glass; each saves the statement amount. */
function initPairIt() {
  const box = document.querySelector("[data-am-pairit]");
  if (!box) return;
  const statement = Number(box.dataset.statement) || 0;
  const base = Number(box.dataset.price) || 0;
  const update = () => {
    const picked = [...box.querySelectorAll("[data-am-addon]:checked")];
    const full = base + picked.reduce((sum, input) => sum + Number(input.dataset.price), 0);
    const save = statement * picked.length;
    box.dispatchEvent(new CustomEvent("am:multibuy", { bubbles: true, detail: { count: picked.length + 1, total: full - save, full, save, mode: "pair" } }));
  };
  box.addEventListener("change", update);
  update();
}

/* Quick view: one dialog (am-product-quickview) for the design swatches, the "All designs" drawer, the "Also available in"
   cards and the pair-it quick looks. The trigger's own title, price and photo paint at once; the lamp's lightweight
   /products/handle?view=am-quick fragment (photos as ready slides, style line, price in the market currency, description)
   is prefetched on hover, focus or touch, cached, and cloned in when it arrives. am-core opens the dialog and posts the forms.
   Triggers that carry data-am-qv-img (the lamp's first photo, 1200w base URL) start that photo on the same hover, focus
   or touch and paint it as the first slide straight away, without waiting for the fragment; the srcset and sizes below
   match the fragment's first slide (templates/product.am-quick.liquid), so both are one cache entry. The other slides and
   the thumbnails hold back until the first photo is sharp, then the neighbours of the shown slide fill in. */
const QV_WIDTHS = [600, 800, 1000, 1200];
const QV_SIZES = "(min-width: 768px) min(51vw, 533px), 100vw";
function initQuickView() {
  const dialog = document.querySelector("[data-am-quickview]");
  if (!dialog) return;
  const el = (name) => dialog.querySelector(`[data-am-qv-${name}]`);
  const track = el("track");
  const thumbs = el("thumbs");
  const countOut = el("count");
  const prev = dialog.querySelector('[data-am-qv-step="-1"]');
  const next = dialog.querySelector('[data-am-qv-step="1"]');
  const desc = el("desc");
  const more = el("more");
  const skeleton = desc.innerHTML;
  const cache = new Map(); // fragment URL -> Promise<Element> (the parsed [data-am-qv-data] node)
  const photos = new Map(); // first photo base URL -> warming Image (kept so the request is not dropped)
  let slides = [];
  let index = 0;
  let token = 0;
  let ready = false; // the first photo is sharp: neighbours and thumbnails may load

  const photoUrl = (trigger) => trigger.dataset.amQvImg || "";
  const photoSet = (url) => QV_WIDTHS.map((w) => `${url.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(", ");
  const warmPhoto = (trigger) => {
    const url = photoUrl(trigger);
    if (!url || photos.has(url)) return;
    const probe = new Image();
    probe.fetchPriority = "high";
    probe.sizes = QV_SIZES;
    probe.srcset = photoSet(url);
    probe.src = url;
    photos.set(url, probe);
  };
  // The first slide, built from the trigger: the same markup the fragment's first slide has.
  const firstSlide = (trigger) => {
    const url = photoUrl(trigger);
    if (!url) return null;
    const figure = document.createElement("figure");
    const file = url.split("?")[0].split("/").pop().toLowerCase();
    figure.className = file.includes("dim_") ? "am-qv__slide am-qv__slide--contain" : "am-qv__slide";
    figure.dataset.index = "0";
    const img = document.createElement("img");
    img.alt = trigger.dataset.amQvTitle || "";
    img.loading = "eager";
    img.fetchPriority = "high";
    img.decoding = "async";
    img.sizes = QV_SIZES;
    img.srcset = photoSet(url);
    img.src = url;
    figure.append(img);
    return figure;
  };
  // Slides after the first and the thumbnails wait (sources parked in data attributes) until the first photo is sharp.
  const park = (img) => {
    if (!img?.getAttribute("srcset")) return;
    img.dataset.srcset = img.getAttribute("srcset");
    img.dataset.src = img.getAttribute("src") || "";
    img.removeAttribute("srcset");
    img.removeAttribute("src");
    img.loading = "eager";
  };
  const fillSlide = (i) => hydrate(slides[i]?.querySelector("img[data-srcset]"));

  const load = (trigger) => {
    const url = `${trigger.pathname.replace(/\/+$/, "")}?view=am-quick`;
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { headers: { Accept: "text/html" } }).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      }).then((html) => {
        const template = document.createElement("template");
        template.innerHTML = html;
        const data = template.content.querySelector("[data-am-qv-data]");
        if (!data) throw new Error("No quick view data");
        // The first photo warms up with the fragment (same srcset and sizes, so the dialog's copy is a cache hit), unless the
        // trigger already started it.
        const first = data.querySelector("[data-am-qv-slides] img");
        if (first && !photos.has(first.getAttribute("src"))) {
          const probe = new Image();
          probe.sizes = first.getAttribute("sizes") || "";
          probe.srcset = first.getAttribute("srcset") || "";
          probe.src = first.getAttribute("src") || "";
        }
        return data;
      }).catch((error) => {
        cache.delete(url);
        throw error;
      }));
    }
    return cache.get(url);
  };
  // Warm the cache before the click: hover or focus on desktop, the first touch on phones.
  const warm = (event) => {
    const trigger = event.target.closest?.("[data-am-qv]");
    if (!trigger) return;
    warmPhoto(trigger);
    load(trigger).catch(() => {});
  };
  document.addEventListener("pointerover", warm, { passive: true });
  document.addEventListener("focusin", warm);
  document.addEventListener("touchstart", warm, { passive: true });
  // The trigger's own small photo (already decoded on the page) covers the track, blurred, until the first sharp photo
  // has been decoded; then it fades out. A 4 s safety timer lifts it even if that photo never arrives.
  const placeholder = document.createElement("img");
  placeholder.className = "am-qv__placeholder";
  placeholder.alt = "";
  placeholder.setAttribute("aria-hidden", "true");
  placeholder.hidden = true;
  let placeholderTimer = 0;
  const hidePlaceholder = () => {
    window.clearTimeout(placeholderTimer);
    if (placeholder.hidden) return;
    placeholder.classList.add("is-gone");
    placeholderTimer = window.setTimeout(() => { placeholder.hidden = true; }, 240);
  };
  const showPlaceholder = (src) => {
    window.clearTimeout(placeholderTimer);
    if (!src) { placeholder.hidden = true; return; }
    placeholder.src = src;
    placeholder.classList.remove("is-gone");
    placeholder.hidden = false;
    placeholderTimer = window.setTimeout(hidePlaceholder, 4000);
  };

  const mark = (i) => {
    index = i;
    if (countOut) countOut.textContent = `${i + 1} / ${slides.length}`;
    if (prev) prev.disabled = i === 0;
    if (next) next.disabled = i === slides.length - 1;
    [...thumbs.children].forEach((thumb, t) => {
      if (t === i) thumb.setAttribute("aria-current", "true");
      else thumb.removeAttribute("aria-current");
    });
    // The shown photo always; the next and previous ones once the first photo is sharp, so a swipe never waits.
    fillSlide(i);
    if (ready) {
      fillSlide(i + 1);
      fillSlide(i - 1);
    }
  };
  const goTo = (i, smooth = true) => {
    const target = slides[clamp(i, 0, slides.length - 1)];
    if (!target) return;
    track.scrollTo({ left: target.offsetLeft, behavior: smooth ? behavior : "auto" });
    mark(slides.indexOf(target));
  };
  const observer = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) mark(slides.indexOf(entry.target));
      }
    }, { root: track, threshold: 0.6 })
    : null;

  // slideNodes and thumbNodes are ready-made <figure> and <button> elements: cloned from the fragment, or built from the trigger's photo.
  const renderMedia = (slideNodes, thumbNodes) => {
    observer?.disconnect();
    track.replaceChildren(...slideNodes, placeholder);
    thumbs.replaceChildren(...thumbNodes);
    slides = slideNodes;
    const many = slides.length > 1;
    if (prev) prev.hidden = !many;
    if (next) next.hidden = !many;
    if (countOut) countOut.hidden = !many;
    track.scrollTo({ left: 0, behavior: "auto" });
    mark(0);
    slides.forEach((slide) => observer?.observe(slide));
  };

  const open = (trigger) => {
    const d = trigger.dataset;
    const my = ++token;
    const available = d.amQvAvailable === "true";
    const eyebrow = el("eyebrow");
    eyebrow.textContent = d.amQvEyebrow || "";
    eyebrow.hidden = !d.amQvEyebrow;
    el("title").textContent = d.amQvTitle || "";
    el("price").textContent = d.amQvPrice || "";
    const save = el("save");
    save.textContent = d.amQvSave || "";
    save.hidden = !d.amQvSave;
    el("soldout").hidden = available;
    el("link").href = trigger.href;

    const addon = el("addon");
    const single = el("form-single");
    const add = el("add");
    if (d.amQvBoth && available) {
      el("id").value = d.amQvBoth;
      addon.value = d.amQvVariant;
      addon.checked = true;
      el("add-label").textContent = d.amQvBothLabel || "Add both to basket";
      el("single-id").value = d.amQvVariant;
      single.hidden = false;
    } else {
      el("id").value = d.amQvVariant;
      addon.value = "";
      addon.checked = false;
      el("add-label").textContent = available ? "Add to basket" : "Sold out";
      single.hidden = true;
    }
    add.disabled = !available;
    dialog.querySelectorAll("[data-am-error]").forEach((box) => { box.textContent = ""; });

    desc.innerHTML = skeleton;
    desc.setAttribute("aria-busy", "true");
    desc.classList.remove("is-clamped");
    more.hidden = true;
    more.setAttribute("aria-expanded", "false");
    dialog.querySelector(".am-qv__body").scrollTop = 0;

    // The trigger's own photo shows at once (a swatch, a card, a pair-it row); the full set replaces it on arrival.
    const cardImg = trigger.querySelector("img") || trigger.closest(".am-vcard, .am-pairit__item, .am-dcard")?.querySelector("img");
    const instant = cardImg?.currentSrc && !cardImg.currentSrc.startsWith("data:") ? cardImg.currentSrc : "";
    // Neighbours and thumbnails load once the first photo is sharp (or failed, or the fragment has no photos).
    ready = false;
    const fillRest = () => {
      thumbs.querySelectorAll("img[data-srcset]").forEach((img) => hydrate(img));
      mark(index);
    };
    const release = () => {
      if (my !== token || ready) return;
      ready = true;
      fillRest();
    };
    let watched = null;
    const watch = (img) => {
      if (!img || img === watched) return;
      watched = img;
      const sharp = () => {
        if (my !== token || img !== watched) return;
        hidePlaceholder();
        performance.measure("am:qv-sharp", "am:qv-open"); // click to first sharp photo, read by the audits
        release();
      };
      img.decode().then(sharp, sharp);
    };
    warmPhoto(trigger);
    const early = firstSlide(trigger);
    // Empty thumbnail squares hold the row's height until the real ones arrive, so the photo never jumps.
    const count = Number(d.amQvPhotos) || 0;
    const holders = Array.from({ length: count > 1 ? count : 0 }, () => {
      const holder = document.createElement("span");
      holder.className = "am-qv__thumb";
      holder.setAttribute("aria-hidden", "true");
      return holder;
    });
    renderMedia(early ? [early] : [], holders);
    showPlaceholder(instant);
    performance.mark("am:qv-open");
    watch(early?.querySelector("img"));

    load(trigger).then((data) => {
      if (my !== token) return;
      const slideNodes = [...data.querySelector("[data-am-qv-slides]").children].map((node) => node.cloneNode(true));
      const thumbNodes = [...data.querySelector("[data-am-qv-thumbs]").children].map((node) => node.cloneNode(true));
      slideNodes.slice(1).forEach((node) => park(node.querySelector("img")));
      thumbNodes.forEach((node) => park(node.querySelector("img")));
      if (slideNodes.length) {
        // The early slide stays in place when it is the same photo (no second request, no flash).
        const first = slideNodes[0].querySelector("img");
        if (early && first && first.getAttribute("src") === early.querySelector("img").getAttribute("src")) slideNodes[0] = early;
        else if (first) {
          first.loading = "eager";
          first.setAttribute("fetchpriority", "high");
        }
        renderMedia(slideNodes, thumbNodes);
        if (ready) fillRest();
        const shown = slideNodes[0].querySelector("img");
        if (shown) watch(shown);
        else {
          hidePlaceholder();
          release();
        }
      } else {
        hidePlaceholder();
        release();
      }
      if (data.dataset.title) el("title").textContent = data.dataset.title;
      if (data.dataset.price) el("price").textContent = data.dataset.price;
      if (data.dataset.eyebrow) {
        eyebrow.textContent = data.dataset.eyebrow;
        eyebrow.hidden = false;
      }
      if (!d.amQvVariant && data.dataset.variant) el("id").value = data.dataset.variant;
      const body = data.querySelector("[data-am-qv-desc]");
      if (body && body.textContent.trim()) desc.replaceChildren(...[...body.childNodes].map((node) => node.cloneNode(true)));
      else desc.innerHTML = "<p>Open the full page for the details.</p>";
      desc.removeAttribute("aria-busy");
      if (desc.scrollHeight > 190) {
        desc.classList.add("is-clamped");
        more.hidden = false;
      }
    }).catch(() => {
      if (my !== token) return;
      if (!early) hidePlaceholder();
      desc.innerHTML = "<p>Open the full page for the details.</p>";
      desc.removeAttribute("aria-busy");
    });
  };
  dialog.addEventListener("close", () => { placeholder.hidden = true; });

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-am-qv]");
    if (!trigger) return;
    // am-core opens the dialog (data-am-open) and prevents the link's navigation; this fills it.
    open(trigger);
  });
  dialog.addEventListener("click", (event) => {
    const step = event.target.closest("[data-am-qv-step]");
    if (step) return goTo(index + Number(step.dataset.amQvStep));
    const thumb = event.target.closest("[data-am-qv-thumb]");
    if (thumb) return goTo(Number(thumb.dataset.amQvThumb));
    if (event.target.closest("[data-am-qv-more]")) {
      const expanded = desc.classList.toggle("is-clamped") === false;
      more.setAttribute("aria-expanded", String(expanded));
      more.firstChild.textContent = expanded ? "Show less" : "Read more";
    }
  });
  dialog.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    if (event.target.closest("input, textarea")) return;
    event.preventDefault();
    goTo(index + (event.key === "ArrowRight" ? 1 : -1));
  });
  // The basket drawer takes over once a form posts, so the quick view steps aside first.
  dialog.addEventListener("submit", () => AM.closeDialog(dialog));

  // The "All designs" drawer closes too when one of its add buttons posts (the basket opens on top).
  const designs = document.getElementById("am-designs");
  if (designs) {
    designs.addEventListener("submit", () => AM.closeDialog(designs));
    designs.addEventListener("click", (event) => {
      if (event.target.closest("[data-am-designs-add]")) AM.closeDialog(designs);
    });
  }
}

/* "Light it up": a before/after slider. --pos is the unlit share from the left; the handle is a real slider. */
function initCompare() {
  const box = document.querySelector("[data-am-compare]");
  if (!box) return;
  const handle = box.querySelector("[data-am-compare-handle]");
  let pos = 50;
  let dragging = false;
  const set = (value) => {
    pos = clamp(Math.round(value * 10) / 10, 0, 100);
    box.style.setProperty("--pos", `${pos}%`);
    handle.setAttribute("aria-valuenow", String(Math.round(pos)));
    const lit = Math.round(100 - pos);
    handle.setAttribute("aria-valuetext", lit <= 2 ? "Switched off" : lit >= 98 ? "Fully lit" : `${lit}% lit`);
    box.classList.add("is-used");
  };
  const fromEvent = (event) => {
    const rect = box.getBoundingClientRect();
    set(((event.clientX - rect.left) / rect.width) * 100);
  };
  box.addEventListener("pointerdown", (event) => {
    if (event.button !== 0 && event.pointerType === "mouse") return;
    dragging = true;
    box.classList.add("is-dragging");
    fromEvent(event);
    try { box.setPointerCapture(event.pointerId); } catch { /* a pointer that is already gone: the move events still arrive */ }
    handle.focus({ preventScroll: true });
    event.preventDefault();
  });
  box.addEventListener("pointermove", (event) => {
    if (dragging) fromEvent(event);
  });
  const stop = () => {
    dragging = false;
    box.classList.remove("is-dragging");
  };
  box.addEventListener("pointerup", stop);
  box.addEventListener("pointercancel", stop);
  handle.addEventListener("keydown", (event) => {
    const big = event.shiftKey ? 10 : 2;
    const moves = { ArrowLeft: pos - big, ArrowDown: pos - big, ArrowRight: pos + big, ArrowUp: pos + big, Home: 0, End: 100, PageDown: pos - 10, PageUp: pos + 10 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    set(moves[event.key]);
  });
}

/* The add button, the sticky bar and the craft card follow the selection. The elements are looked up on every
   event: am-core's busy state restores a button's children from clones after each add, so cached nodes go stale. */
function initBuyLabels() {
  document.addEventListener("am:multibuy", (event) => {
    const { count, total, mode } = event.detail;
    const many = count > 1;
    const noun = mode === "pair" ? "" : " lamps";
    const long = many ? `Add ${count}${noun} to basket` : "Add to basket";
    const set = (selector, text) => document.querySelectorAll(selector).forEach((el) => { el.textContent = text; });
    set("[data-am-buy-label]", long);
    document.querySelectorAll("[data-am-buy-price]").forEach((price) => {
      price.hidden = !many;
      price.textContent = many ? `· ${AM.formatMoney(total)}` : "";
    });
    set("[data-am-sticky-price]", AM.formatMoney(total));
    set("[data-am-sticky-label]", many ? `Add ${count}${noun}` : "Add to basket");
    set("[data-am-craft-label]", long);
  });
}

function initSticky() {
  const bar = document.querySelector("[data-am-sticky]");
  const button = document.querySelector("[data-am-buy-button]");
  if (!bar || !button || !("IntersectionObserver" in window)) return;
  // Shown once the main button has scrolled off the top of the screen, so a buy button stays one tap away, and hidden
  // again while the "This lamp" card (Made by hand) is on screen: that card has its own add button and must never sit under the bar.
  const card = document.querySelector("[data-am-craft-button]")?.closest(".am-craft2__card") || document.querySelector(".am-craft2__card");
  let past = false;
  let cardSeen = false;
  const paint = () => {
    const show = past && !cardSeen;
    bar.hidden = !show;
    document.documentElement.classList.toggle("am-has-sticky", show);
  };
  new IntersectionObserver(([entry]) => {
    past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    paint();
  }).observe(button);
  if (card) {
    new IntersectionObserver(([entry]) => {
      cardSeen = entry.isIntersecting;
      paint();
    }, { threshold: 0 }).observe(card);
  }
}

/* "Made by hand": the story column is sticky from 1024px. When it is taller than the room under the header, it sticks
   by its bottom instead (top = viewport - height - 24px, measured by a ResizeObserver), so the "This lamp" card is
   always fully in view; the buy bar already hides while the card is on screen (initSticky). */
function initCraftSticky() {
  const story = document.querySelector(".am-craft2__story");
  if (!story || !("ResizeObserver" in window)) return;
  let frame = 0;
  const place = () => {
    frame = 0;
    if (!desktop.matches) {
      story.style.removeProperty("--story-top");
      return;
    }
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--h-row1")) || 84;
    // Shopify's preview bar (theme previews only) sits over the bottom 56px; the live site has no such iframe.
    const gap = 24 + (document.getElementById("PBarNextFrame") ? 56 : 0);
    const top = Math.min(header + 16, window.innerHeight - story.offsetHeight - gap);
    story.style.setProperty("--story-top", `${Math.round(top)}px`);
  };
  const queue = () => { if (!frame) frame = window.requestAnimationFrame(place); };
  new ResizeObserver(queue).observe(story);
  window.addEventListener("resize", queue, { passive: true });
  desktop.addEventListener("change", queue);
  place();
}

/* Reviews: 9 cards show at first; "Show more" reveals the next 9 (the hidden cards' photos stay lazy until then). */
function initReviews() {
  const button = document.querySelector("[data-am-reviews-more]");
  if (!button) return;
  const hidden = () => [...document.querySelectorAll(".am-review[hidden]")];
  button.addEventListener("click", () => {
    const batch = hidden().slice(0, 9);
    batch.forEach((card) => { card.hidden = false; });
    batch[0]?.querySelector("a, button, [tabindex]")?.focus?.();
    const left = hidden().length;
    if (!left) button.hidden = true;
    else button.querySelector("[data-am-reviews-left]").textContent = String(left);
    AM.announce?.(`${batch.length} more reviews shown`);
  });
}

/* Lazy photos rendered by shared snippets (am-image-by-role) carry no decoding attribute: set it before they load,
   so decoding never blocks the main thread while the page scrolls. */
function asyncDecoding() {
  document.querySelectorAll('img[loading="lazy"]:not([decoding])').forEach((img) => { img.decoding = "async"; });
}

function londonNow() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  // A UTC date carrying London's wall-clock time, so day arithmetic ignores the viewer's time zone.
  return new Date(Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute")));
}

/* Delivery estimate. Dispatch: on a working day (Monday to Friday, not an England and Wales bank holiday) before the
   cut-off, otherwise the next such day. UK (Royal Mail Tracked 24): ukDays delivery days, counting Monday to Saturday and
   skipping bank holidays. Elsewhere: intlMin to intlMax working days after dispatch. AM.delivery.estimate(date) exposes
   the maths so it can be checked for any moment (the date carries London wall-clock time as UTC, like londonNow()). */
const DAY_FORMAT = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
const SHORT_FORMAT = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "long", timeZone: "UTC" });
function deliveryEstimate(now = londonNow()) {
  const cutoff = Number(AM.delivery.cutoff) || 15;
  const ukDays = Number(AM.delivery.ukDays) || 1;
  const intlMin = Number(AM.delivery.intlMin) || 3;
  const intlMax = Math.max(intlMin, Number(AM.delivery.intlMax) || 7);
  const holidays = new Set(Array.isArray(AM.delivery.holidays) ? AM.delivery.holidays : []);
  const holiday = (d) => holidays.has(d.toISOString().slice(0, 10));
  const workingDay = (d) => d.getUTCDay() !== 0 && d.getUTCDay() !== 6 && !holiday(d);
  const ukDeliveryDay = (d) => d.getUTCDay() !== 0 && !holiday(d); // Tracked 24 delivers Monday to Saturday
  const after = (from, days, ok) => {
    const d = new Date(from);
    for (let added = 0; added < days; ) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (ok(d)) added += 1;
    }
    return d;
  };
  const sameDay = workingDay(now) && now.getUTCHours() < cutoff;
  const dispatch = sameDay ? new Date(now) : after(now, 1, workingDay);
  const left = cutoff * 60 - (now.getUTCHours() * 60 + now.getUTCMinutes());
  const hours = Math.floor(left / 60);
  const minutes = left % 60;
  return {
    sameDay,
    wait: sameDay ? (hours ? `${hours} h ${minutes} min` : `${minutes} min`) : "",
    dispatch,
    uk: after(dispatch, ukDays, ukDeliveryDay),
    intlMin: after(dispatch, intlMin, workingDay),
    intlMax: after(dispatch, intlMax, workingDay),
  };
}

function initDelivery() {
  if (!AM.delivery) return;
  AM.delivery.estimate = deliveryEstimate;
  const estimate = deliveryEstimate();

  const stock = document.querySelector("[data-am-craft-stock]");
  if (stock && estimate.sameDay) stock.textContent = "In stock, ships today";

  const uk = document.querySelector('[data-am-delivery][data-mode="uk"]');
  if (uk) {
    const day = DAY_FORMAT.format(estimate.uk);
    uk.textContent = estimate.sameDay ? `Order within ${estimate.wait} for estimated delivery on ${day}` : `Estimated delivery on ${day}`;
  }
  const sub = document.querySelector("[data-am-delivery-sub]");
  if (document.querySelector('[data-am-delivery][data-mode="intl"]') && sub) {
    // The first line (the working-day window, "Express" for the US and Canada) is rendered by Liquid; this adds the dates.
    const first = SHORT_FORMAT.format(estimate.intlMin);
    const last = SHORT_FORMAT.format(estimate.intlMax);
    const sameMonth = estimate.intlMin.getUTCMonth() === estimate.intlMax.getUTCMonth();
    const arrival = `${sameMonth ? first.replace(/ \S+$/, "") : first} to ${last}`;
    const lead = estimate.sameDay ? `Order within ${estimate.wait} for dispatch today` : `Dispatched ${SHORT_FORMAT.format(estimate.dispatch)}`;
    sub.textContent = `${lead} · estimated arrival ${arrival} · delivery cost calculated at checkout`;
  }
}

function initBackInStock() {
  const form = document.querySelector("[data-am-bis]");
  if (!form) return;
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const input = form.elements.email;
    const error = form.querySelector("[data-am-error]");
    const done = form.querySelector("[data-am-bis-done]");
    const button = form.querySelector('[type="submit"]');
    const email = input.value.trim();
    error.textContent = "";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      error.textContent = "Enter a valid email address, for example name@example.com.";
      input.setAttribute("aria-invalid", "true");
      input.focus();
      return;
    }
    input.removeAttribute("aria-invalid");
    const label = button.textContent;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.textContent = "Sending…";
    try {
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(AM.klaviyo)}`, {
        method: "POST",
        headers: { "Content-Type": "application/vnd.api+json", revision: "2026-07-15" },
        body: JSON.stringify({
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              profile: { data: { type: "profile", attributes: { email } } },
              channels: ["EMAIL"],
            },
            relationships: {
              variant: { data: { type: "catalog-variant", id: `$shopify:::$default:::${form.dataset.variantId}` } },
            },
          },
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      form.querySelector(".am-inline-form").hidden = true;
      done.hidden = false;
      done.focus();
    } catch {
      error.textContent = "Your request did not go through. Check your connection and try again.";
      button.disabled = false;
      button.removeAttribute("aria-busy");
      button.textContent = label;
    }
  });
}

function rememberView() {
  const handle = root?.dataset.handle;
  if (!handle) return;
  try {
    const key = "am:viewed";
    const list = JSON.parse(window.localStorage.getItem(key) || "[]").filter((h) => h !== handle);
    list.unshift(handle);
    window.localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
  } catch {
    // Storage can be unavailable (private mode); nothing depends on it.
  }
}

initGallery();
initBuyLabels();
initMultibuy();
initPairIt();
initQuickView();
initCompare();
initSticky();
initCraftSticky();
initReviews();
asyncDecoding();
initDelivery();
initBackInStock();
rememberView();
